import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Info as InfoIcon, WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const WhatsAppSettings = () => {
  const { user } = useAuth();
  const companyId = user?.companyId;
  const [templates, setTemplates] = useState({
    maintenanceCompletion: '',
    maintenanceRegistration: '',
    pickupReminder: '',
    estimateReady: '',
    maintenanceReview: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // Add refs for each template text field
  const textFieldRefs = useRef({
    maintenanceCompletion: null,
    maintenanceRegistration: null,
    pickupReminder: null,
    estimateReady: null,
    maintenanceReview: null
  });

  // Template descriptions with variable information
  const templateInfo = {
    maintenanceCompletion: {
      title: 'Completamento Manutenzione',
      description: 'Messaggio inviato quando una manutenzione è completata',
      variables: [
        { name: '{customerName}', label: 'Nome Cliente', description: 'Nome del cliente' },
        { name: '{protocolNumber}', label: 'Numero Manutenzione', description: 'Numero di protocollo della manutenzione' },
        { name: '{vehicleModel}', label: 'Modello Veicolo', description: 'Modello del veicolo' },
        { name: '{problemDescription}', label: 'Descrizione Problema', description: 'Descrizione del problema' },
        { name: '{totalAmount}', label: 'Importo Totale', description: 'Importo totale da pagare' },
        { name: '{deposit}', label: 'Acconto', description: 'Importo dell\'acconto versato' },
        { name: '{termsLink}', label: 'Link Termini', description: 'Link ai termini e condizioni' },
        { name: '{reviewLink}', label: 'Link Recensione', description: 'Link per lasciare una recensione' }
      ],
      defaultTemplate: 'Salve {customerName}, la informiamo che la sua bicicletta è pronta per il ritiro. Protocollo: {protocolNumber}. Grazie!'
    },
    maintenanceRegistration: {
      title: 'Registrazione Manutenzione',
      description: 'Messaggio inviato quando viene registrata una nuova manutenzione',
      variables: [
        { name: '{customerName}', label: 'Nome Cliente', description: 'Nome del cliente' },
        { name: '{protocolNumber}', label: 'Numero Manutenzione', description: 'Numero di protocollo della manutenzione' },
        { name: '{vehicleModel}', label: 'Modello Veicolo', description: 'Modello del veicolo' },
        { name: '{problemDescription}', label: 'Descrizione Problema', description: 'Descrizione del problema' },
        { name: '{deposit}', label: 'Acconto', description: 'Importo dell\'acconto versato' },
        { name: '{termsLink}', label: 'Link Termini', description: 'Link ai termini e condizioni' }
      ],
      defaultTemplate: 'Salve {customerName}, la informiamo che la sua richiesta di manutenzione è stata registrata con il numero di protocollo {protocolNumber}. Veicolo: {vehicleModel}. Problema: {problemDescription}. Acconto versato: {deposit}€. Per i termini di servizio: {termsLink}. La contatteremo appena sarà pronta.'
    },
    pickupReminder: {
      title: 'Sollecito Ritiro',
      description: 'Messaggio inviato per sollecitare il ritiro di una bicicletta',
      variables: [
        { name: '{customerName}', label: 'Nome Cliente', description: 'Nome del cliente' },
        { name: '{protocolNumber}', label: 'Numero Manutenzione', description: 'Numero di protocollo della manutenzione' },
        { name: '{daysInShop}', label: 'Giorni in Officina', description: 'Giorni di giacenza in officina' },
        { name: '{vehicleModel}', label: 'Modello Veicolo', description: 'Modello del veicolo' },
        { name: '{totalAmount}', label: 'Importo Totale', description: 'Importo totale da pagare' },
        { name: '{deposit}', label: 'Acconto', description: 'Importo dell\'acconto versato' },
        { name: '{termsLink}', label: 'Link Termini', description: 'Link ai termini e condizioni' }
      ],
      defaultTemplate: 'Salve {customerName}, le ricordiamo che la sua bicicletta (protocollo: {protocolNumber}) è pronta per il ritiro da {daysInShop} giorni. La preghiamo di passare a ritirarla al più presto. Grazie!'
    },
    estimateReady: {
      title: 'Preventivo Pronto',
      description: 'Messaggio inviato quando un preventivo è pronto',
      variables: [
        { name: '{customerName}', label: 'Nome Cliente', description: 'Nome del cliente' },
        { name: '{protocolNumber}', label: 'Numero Manutenzione', description: 'Numero di protocollo della manutenzione' },
        { name: '{estimateAmount}', label: 'Importo Preventivo', description: 'Importo del preventivo' },
        { name: '{vehicleModel}', label: 'Modello Veicolo', description: 'Modello del veicolo' },
        { name: '{problemDescription}', label: 'Descrizione Problema', description: 'Descrizione del problema' },
        { name: '{deposit}', label: 'Acconto', description: 'Importo dell\'acconto versato' },
        { name: '{termsLink}', label: 'Link Termini', description: 'Link ai termini e condizioni' },
        { name: '{reviewLink}', label: 'Link Recensione', description: 'Link per lasciare una recensione' }
      ],
      defaultTemplate: 'Salve {customerName}, il preventivo per la sua bicicletta (protocollo: {protocolNumber}) è pronto. Il costo stimato è di €{estimateAmount}. La preghiamo di contattarci per confermare o per eventuali chiarimenti.'
    },
    maintenanceReview: {
      title: 'Recensioni (dopo consegna)',
      description: 'Messaggio automatico quando la manutenzione passa a "Consegnato" e disponibile nello storico vendite. Configura il link recensioni in Impostazioni → Informazioni azienda.',
      variables: [
        { name: '{customerName}', label: 'Nome Cliente', description: 'Nome del cliente' },
        { name: '{protocolNumber}', label: 'Protocollo / Riferimento', description: 'Numero protocollo manutenzione o riferimento vendita' },
        { name: '{vehicleModel}', label: 'Modello Veicolo', description: 'Modello veicolo (manutenzione)' },
        { name: '{totalAmount}', label: 'Importo Totale', description: 'Importo totale' },
        { name: '{deposit}', label: 'Acconto', description: 'Acconto' },
        { name: '{termsLink}', label: 'Link Termini', description: 'Link termini e condizioni' },
        { name: '{reviewLink}', label: 'Link Recensione', description: 'URL recensioni (da Impostazioni azienda)' }
      ],
      defaultTemplate: 'Salve {customerName}, grazie per aver scelto il nostro servizio! Se vuole lasciarci una recensione: {reviewLink}. Grazie ancora!'
    }
  };

  useEffect(() => {
    if (!companyId) return;
    fetchTemplates();
  }, [companyId]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get('/company-settings');
      const newTemplates = { ...templates };
      Object.keys(templateInfo).forEach(key => {
        const settingKey = `whatsappTemplate_${key}`;
        if (res.data && res.data[settingKey]) {
          newTemplates[key] = res.data[settingKey];
        } else {
          newTemplates[key] = templateInfo[key].defaultTemplate;
        }
      });
      setTemplates(newTemplates);
    } catch (err) {
      console.error('Error fetching WhatsApp templates:', err);
      const defaultTemplates = {};
      Object.keys(templateInfo).forEach(key => {
        defaultTemplates[key] = templateInfo[key].defaultTemplate;
      });
      setTemplates(defaultTemplates);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (templateKey, value) => {
    setTemplates(prev => ({
      ...prev,
      [templateKey]: value
    }));
  };

  const handleSaveTemplate = async (templateKey) => {
    setSaving(true);
    try {
      await api.patch('/company-settings', {
        [`whatsappTemplate_${templateKey}`]: templates[templateKey]
      });
      showSnackbar(`Template "${templateInfo[templateKey].title}" salvato con successo`);
      window.dispatchEvent(new Event('whatsappTemplateUpdated'));
    } catch (err) {
      console.error(`Error saving WhatsApp template ${templateKey}:`, err);
      showSnackbar(`Errore nel salvataggio del template "${templateInfo[templateKey].title}"`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResetTemplate = (templateKey) => {
    if (window.confirm(`Sei sicuro di voler ripristinare il template "${templateInfo[templateKey].title}" al valore predefinito?`)) {
      handleInputChange(templateKey, templateInfo[templateKey].defaultTemplate);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Function to insert variable at cursor position
  const insertVariableAtCursor = (templateKey, variableName) => {
    const textField = textFieldRefs.current[templateKey];
    if (!textField) return;

    const input = textField.querySelector('textarea');
    if (!input) return;

    const { selectionStart, selectionEnd } = input;
    const currentValue = templates[templateKey];
    // Add asterisks before and after the variable name
    const variableWithAsterisks = `*${variableName}*`;
    const newValue = currentValue.substring(0, selectionStart) + variableWithAsterisks + currentValue.substring(selectionEnd);
    
    handleInputChange(templateKey, newValue);
    
    // Set focus back to the input and place cursor after the inserted variable
    setTimeout(() => {
      input.focus();
      const newCursorPosition = selectionStart + variableWithAsterisks.length;
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const renderVariableHelp = (variables, templateKey) => (
    <Box sx={{ mt: 1, mb: 2, bgcolor: 'background.paper', p: 1, borderRadius: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Variabili disponibili:
      </Typography>
      <Grid container spacing={1} sx={{ mt: 0.5 }}>
        {variables.map((variable, index) => (
          <Grid item key={index}>
            <Tooltip title={variable.description}>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  // Insert variable directly into the text field
                  insertVariableAtCursor(templateKey, variable.name);
                  showSnackbar(`Variabile ${variable.label} inserita nel testo`);
                }}
              >
                {variable.label}
              </Box>
            </Tooltip>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <WhatsAppIcon sx={{ mr: 1, color: '#25D366' }} />
        <Typography variant="h6">Personalizzazione Messaggi WhatsApp</Typography>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Personalizza i modelli di messaggio WhatsApp per diverse situazioni. Utilizza le variabili disponibili per inserire informazioni dinamiche nei messaggi.
          </Typography>
          
          {Object.keys(templateInfo).map((templateKey) => (
            <Accordion key={templateKey} sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{templateInfo[templateKey].title}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {templateInfo[templateKey].description}
                </Typography>
                
                {renderVariableHelp(templateInfo[templateKey].variables, templateKey)}
                
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Testo del messaggio"
                  value={templates[templateKey]}
                  onChange={(e) => handleInputChange(templateKey, e.target.value)}
                  variant="outlined"
                  sx={{ mb: 2 }}
                  ref={el => textFieldRefs.current[templateKey] = el}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    color="secondary"
                    onClick={() => handleResetTemplate(templateKey)}
                  >
                    Ripristina Default
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={() => handleSaveTemplate(templateKey)}
                    disabled={saving}
                  >
                    {saving ? 'Salvataggio...' : 'Salva'}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default WhatsAppSettings;